import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LogIn, Users, ArrowLeft, Eye, EyeOff, AlertCircle, X, Building2, Mail, RefreshCw, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { authService } from '@/services/authService'
import { BRANDING } from '@/config/branding'
import { RESUME_PENDING_ACTION_PATH, rememberPostAuthReturnUrl, takePostAuthReturnUrl } from '@/lib/returnUrl'

const LOGIN_ERROR_KEY = 'login_error'
const PENDING_VERIFICATION_EMAIL_KEY = 'pending_verification_email'

// The backend rejects /security/login for unverified accounts with a 401
// and a plain message (e.g. "Email address not verified. Please verify
// your email before logging in.") — there is no structured error code, so
// detect it by message content rather than relying on a flag that isn't
// actually sent on this path.
function isUnverifiedEmailMessage(message: string | undefined | null): boolean {
  return !!message && /not verified/i.test(message)
}

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthStore()
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation('common')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(() => {
    // Initialize from sessionStorage
    return sessionStorage.getItem(LOGIN_ERROR_KEY)
  })
  const [isLoading, setIsLoading] = useState(false)
  const [justVerified, setJustVerified] = useState(false)

  // Unverified-account inline verification box state. Persisted in sessionStorage
  // so a refresh while the box is showing doesn't lose it (unlike the old
  // /verify-email redirect, which relied only on ephemeral router state).
  const [needsVerification, setNeedsVerification] = useState<boolean>(() => (
    !!sessionStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY)
  ))
  const [verificationEmail, setVerificationEmail] = useState<string>(() => (
    sessionStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY) || ''
  ))
  const [verificationCode, setVerificationCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [sendingCode, setSendingCode] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)

  useEffect(() => {
    // Pre-fill email from location state if available
    if (location.state?.email) {
      setEmail(location.state.email)
    }
  }, [location.state])

  const handleDismissError = () => {
    sessionStorage.removeItem(LOGIN_ERROR_KEY)
    setError(null)
  }

  const startVerification = (verifyEmail: string) => {
    sessionStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, verifyEmail)
    setVerificationEmail(verifyEmail)
    setVerificationCode('')
    setVerifyError(null)
    setResendMessage(null)
    setCodeSent(false)
    setNeedsVerification(true)
  }

  const handleCancelVerification = () => {
    sessionStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY)
    setNeedsVerification(false)
    setVerificationCode('')
    setVerifyError(null)
    setResendMessage(null)
    setCodeSent(false)
  }

  const handleSendCode = async () => {
    setSendingCode(true)
    setVerifyError(null)
    setResendMessage(null)

    try {
      const response = await authService.resendVerificationCode(verificationEmail)
      if (response.success) {
        setCodeSent(true)
        setResendMessage(t('verificationCodeSent'))
      } else {
        setVerifyError(response.message || t('failedToResendCode'))
      }
    } catch (err: any) {
      setVerifyError(err?.response?.data?.message || t('failedToResendCode'))
    } finally {
      setSendingCode(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setVerifyError(null)

    if (!verificationCode || verificationCode.length < 4) {
      setVerifyError(t('enterValidVerificationCode'))
      return
    }

    setVerifying(true)

    try {
      const response = await authService.verifyEmail(verificationEmail, verificationCode)

      if (response.success) {
        sessionStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY)
        setNeedsVerification(false)
        setVerificationCode('')
        setCodeSent(false)
        setResendMessage(null)
        setEmail(verificationEmail)
        setPassword('')
        setJustVerified(true)
      } else {
        setVerifyError(response.message || t('verificationFailed'))
      }
    } catch (err: any) {
      setVerifyError(err?.response?.data?.message || t('verificationFailed'))
    } finally {
      setVerifying(false)
    }
  }

  const handleEmployeeLogin = () => {
    // Pending checkout actions only replay through /login/callback, which then
    // honors redirectAfterLogin, so SSO has to route there instead of the page.
    rememberPostAuthReturnUrl(
      localStorage.getItem('pendingAction')
        ? RESUME_PENDING_ACTION_PATH
        : localStorage.getItem('redirectAfterLogin') || location.state?.returnTo || takePostAuthReturnUrl(),
    )
    window.location.href = `${window.location.origin}/admin`
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    sessionStorage.removeItem(LOGIN_ERROR_KEY)
    setError(null)
    setJustVerified(false)

    if (!email || !password) {
      const msg = t('enterBothEmailAndPassword')
      sessionStorage.setItem(LOGIN_ERROR_KEY, msg)
      setError(msg)
      return
    }

    setIsLoading(true)
    
    try {
      const response = await login(email, password)
      setIsLoading(false)

      // Check this before the success check: some backends flag unverified
      // accounts on an otherwise-failed response (success: false), and the
      // old code returned on !response.success before ever seeing this flag.
      if (response.requiresEmailVerification === true) {
        startVerification(email)
        return
      }

      if (!response.success) {
        // Login failed
        const errorMessage = response.message || t('loginFailed')

        if (isUnverifiedEmailMessage(errorMessage)) {
          startVerification(email)
          return
        }

        sessionStorage.setItem(LOGIN_ERROR_KEY, errorMessage)
        setError(errorMessage)
        return
      }
      
      // Login succeeded - clear any errors
      sessionStorage.removeItem(LOGIN_ERROR_KEY)

      const rememberedReturnUrl = takePostAuthReturnUrl()

      if (localStorage.getItem('pendingAction')) {
        navigate(RESUME_PENDING_ACTION_PATH)
        return
      }
      
      // Check for redirect URL
      const redirectUrl = localStorage.getItem('redirectAfterLogin') || location.state?.returnTo
        || (rememberedReturnUrl !== RESUME_PENDING_ACTION_PATH ? rememberedReturnUrl : undefined)
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin')
        navigate(redirectUrl)
      } else {
        // Default to home
        navigate('/')
      }
      
    } catch (err: any) {
      setIsLoading(false)
      const errorMessage = err?.response?.data?.message || err?.message || t('loginFailed')

      // The backend rejects unverified accounts at /security/login itself
      // with a 401 and a plain-text message (no structured flag), so this
      // is the only signal available to route into the verification box.
      if (isUnverifiedEmailMessage(errorMessage)) {
        startVerification(email)
        return
      }

      sessionStorage.setItem(LOGIN_ERROR_KEY, errorMessage)
      setError(errorMessage)
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-white">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Back to Home Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t('backToHome')}
        </button>
        
        <div className="flex justify-center items-center">
          <img
            src={BRANDING.appLogo}
            alt={BRANDING.appName}
            className="h-16 w-auto"
          />
        </div>
        <p className="mt-4 text-center text-sm text-gray-600">
          {t('signInSubtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        {needsVerification ? (
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: BRANDING.primaryColor }}>
                <Mail className="h-7 w-7 text-white" />
              </div>
            </div>
            <h2 className="text-center text-xl font-bold text-gray-900">
              {t('emailNotVerified')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('emailNotVerifiedMessage', { email: verificationEmail })}
            </p>

            <div className="mt-6 space-y-6">
              {verifyError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                    <span className="text-red-800 text-sm">{verifyError}</span>
                  </div>
                </div>
              )}

              {resendMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-green-800 text-sm">{resendMessage}</span>
                  </div>
                </div>
              )}

              {!codeSent ? (
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={sendingCode}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ backgroundColor: BRANDING.primaryColor, '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                >
                  {sendingCode ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-white mr-2"></div>
                      {tc('sending')}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2" />
                      {t('sendVerificationCode')}
                    </div>
                  )}
                </button>
              ) : (
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  <div>
                    <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                      {t('verificationCode')}
                    </label>
                    <div className="mt-1">
                      <input
                        id="verificationCode"
                        name="verificationCode"
                        type="text"
                        required
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                        style={{ '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                        placeholder={t('enterCode')}
                        maxLength={10}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={verifying}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{ backgroundColor: BRANDING.primaryColor, '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                  >
                    {verifying ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-white mr-2"></div>
                        {tc('verifying')}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {t('verifyEmail')}
                      </div>
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={sendingCode}
                      className="text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ color: BRANDING.primaryColor }}
                    >
                      {sendingCode ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-transparent border-t-current mr-2"></div>
                          {tc('sending')}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <RefreshCw className="h-3 w-3 mr-1" />
                          {t('resendVerificationCode')}
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleCancelVerification}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  {t('backToLogin')}
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleLogin} className="space-y-6">
            {justVerified && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                  <span className="text-green-800 text-sm">{t('emailVerifiedSuccessfully')} {t('signInWithExisting')}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg p-4 bg-red-50 border border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 text-red-600" />
                    <span className="text-sm text-red-800">{error}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleDismissError}
                    className="ml-3 flex-shrink-0 text-red-600 hover:text-red-800 transition-colors"
                    aria-label={t('dismissError')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('emailAddress')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm"
                  style={{ '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                  placeholder={t('enterEmail')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('password')}
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm pr-10"
                  style={{ '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                  placeholder={t('enterPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="font-medium transition-colors"
                  style={{ color: BRANDING.primaryColor }}
                >
                  {t('forgotPassword')}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{ backgroundColor: BRANDING.primaryColor, '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                onMouseEnter={(e) => !isLoading && (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={(e) => !isLoading && (e.currentTarget.style.opacity = '1')}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-transparent border-t-white mr-2"></div>
                    {t('signingIn')}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <LogIn className="h-4 w-4 mr-2" />
                    {t('signIn')}
                  </div>
                )}
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">{tc('or')}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate('/register')}
                className="w-full flex justify-center py-3 px-4 border rounded-md shadow-sm text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
                style={{ borderColor: BRANDING.primaryColor, color: BRANDING.primaryColor, '--tw-ring-color': BRANDING.primaryColor } as React.CSSProperties}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85' }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
              >
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  {t('createNewAccount')}
                </div>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-400">{tc('or')}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleEmployeeLogin}
                className="w-full flex justify-center py-3 px-4 border rounded-md shadow-sm text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
                style={{ borderColor: BRANDING.secondaryColor, color: BRANDING.secondaryColor, '--tw-ring-color': BRANDING.secondaryColor } as React.CSSProperties}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85' }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
              >
                <div className="flex items-center">
                  <Building2 className="h-4 w-4 mr-2" />
                  {t('employeeLogin')}
                </div>
              </button>
            </div>
          </form>
        </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            {t('termsNotice')}
          </p>
        </div>
      </div>
    </div>
  )
}
